import React from 'react';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import find from 'lodash/find';
import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import { Typography, Paper, Tooltip } from '@mui/material';
import UILink from '@mui/material/Link';
import { connect } from 'react-redux';
import { nanoid } from 'nanoid';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import MUIDataTable from 'mui-datatables';
import { bindActionCreators } from 'redux';

import RepairTicketEditModal from './repair-ticket-edit-modal';
import CustomToolbar from './custom-toolbar';
import withPersistentSettings from '../common/table-with-persistent-settings';
import WrappedSpinner from '../common/wrapped-spinner';
import DateRangeFilter from '../common/date-range-filter';
import StatusPill from '../common/status-pill';
import {
  shouldFetchList,
  sortByIntegerString,
  getUrlSearchResults,
  timeZoneHint,
  localeTimeFormat,
  localeDateFormat,
  CustomTooltip,
} from '../../lib/utils';
import { NOT_DIAGNOSED_ID } from '../../lib/repair-ticket-categories';
import { shouldExcludeDate, filterChipText } from '../../lib/table-utils';
import VehicleEditForm from './vehicle-edit-form';
import marketActions from '../../actions/markets';
import repairTicketActions from '../../actions/repairTickets';
import vehicleActions from '../../actions/vehicles';
import * as snackbarActions from '../../actions/snackbar';
import {
  REPAIR_VEHICLE_TYPE,
} from '../../lib/routes/routeHelper';
import withUrlProperties from '../common/url-props';

dayjs.extend(timezone);

const styles = {
  root: {
    width: '95%',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'auto',
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
};

const OPEN = 'Open';
const CREATED_AT = 'Created At';
const CLOSED_AT = 'Closed At';
const DAYS_OPEN = 'Days Open';
const STATUS = 'Status';
const DESCRIPTION = 'Description';
const CREATED_BY = 'Created By';
const CLOSED_BY = 'Closed By';
const VEHICLE = 'Vehicle';

class RepairTicketsTable extends React.Component {
  constructor(props) {
    super(props);

    const urlParams = getUrlSearchResults();

    let selectedVehicleTypes;
    const urlType = get(urlParams, REPAIR_VEHICLE_TYPE);
    if (urlType) {
      if (isArray(urlType)) {
        selectedVehicleTypes = urlType;
      } else {
        selectedVehicleTypes = [urlType];
      }
    }

    this.state = {
      isRepairTicketFormOpen: false,
      editRepairTicket: { },
      editing: false,
      selectedVehicleTypes,
      selectedVehicleBrands: undefined,
      editVehicle: {},
      showEditVehicleModal: false,
    };

    this.handleViewRepairTicket = this.handleViewRepairTicket.bind(this);
    this.handleCloseForm = this.handleCloseForm.bind(this);
    this.handleSaveForm = this.handleSaveForm.bind(this);
    this.handleAddRepairTicket = this.handleAddRepairTicket.bind(this);
    this.handleOpenModalForRepairInURL = this.handleOpenModalForRepairInURL.bind(this);
    this.openVehicleModal = this.openVehicleModal.bind(this);
    this.handleCloseVehicleModal = this.handleCloseVehicleModal.bind(this);
    this.handleSaveVehicleModal = this.handleSaveVehicleModal.bind(this);
  }

  componentDidMount() {
    const {
      repairTickets, fetchRepairTickets, vehicles, fetchVehicles, match,
    } = this.props;
    const marketId = get(match, 'params.marketId');

    const repairTicketId = get(match, 'params.repairTicketId');
    if (repairTicketId) {
      const targetRepairTicket = find(repairTickets, (ticket) => ticket._id === repairTicketId);
      if (targetRepairTicket) this.handleViewRepairTicket(targetRepairTicket._id);
    }

    if (marketId) {
      if (shouldFetchList(marketId, repairTickets)) fetchRepairTickets({ market_id: marketId });
      if (shouldFetchList(marketId, vehicles)) fetchVehicles({ market_id: marketId });
    }
    // check if repairId is in URL and open modal if so
    this.handleOpenModalForRepairInURL();
  }

  get repairTickets() {
    const { selectedVehicleTypes, selectedVehicleBrands } = this.state;
    let repairTickets = get(this, 'props.repairTickets', []);
    if (selectedVehicleTypes) {
      repairTickets = repairTickets.filter(
        (ticket) => includes(selectedVehicleTypes, get(ticket, 'vehicle.type', [])),
      );
    }
    if (selectedVehicleBrands) {
      repairTickets = repairTickets.filter(
        (ticket) => includes(selectedVehicleBrands, get(ticket, 'vehicle.brand', [])),
      );
    }

    return repairTickets;
  }

  get tableOptions() {
    const {
      tableOptions: basicOptions,
    } = this.props;

    basicOptions.onRowClick = (rowData) => {
      this.handleViewRepairTicket(rowData[0]);
    };

    basicOptions.customToolbar = () => (
      <CustomToolbar onClick={this.handleAddRepairTicket} type="Add" />
    );

    const previousOnFilterChange = basicOptions.onFilterChange;
    basicOptions.onFilterChange = (changedColumn, filterList) => {
      if (previousOnFilterChange) previousOnFilterChange(changedColumn, filterList);
    };

    return basicOptions;
  }

  get columns() {
    const {
      selectedFilters, primarySort, addToColumnData, setUrlColumnFilters,
      selectedMarket,
    } = this.props;
    const tz = get(selectedMarket, 'tz', 'America/New_York');

    const columnData = [
      {
        name: 'id',
        options: {
          filter: false,
          display: 'excluded',
        },
      },
      {
        name: CREATED_AT,
        options: {
          customBodyRender: (value) => {
            if (value) {
              return (
                <>
                  <Typography noWrap>
                    {moment.tz(value, tz).format(localeDateFormat)}
                  </Typography>
                  <Typography>{dayjs(value).tz(tz).format(localeTimeFormat)}</Typography>
                </>
              );
            } else {
              return '';
            }
          },
          filterType: 'custom',
          customFilterListOptions: {
            render: (dates) => filterChipText(dates, CREATED_AT),
          },
          filterOptions: {
            logic: (date, filters) => shouldExcludeDate(date, filters),
            display: (filterList, onChange, index, column) => (
              <DateRangeFilter
                filterList={filterList}
                onChange={onChange}
                index={index}
                column={column}
                name={CREATED_AT}
              />
            ),
          },
          hint: timeZoneHint(tz),
        },
      },
      {
        name: CLOSED_AT,
        options: {
          customBodyRender: (value) => {
            if (value) {
              return (
                <>
                  <Typography noWrap>
                    {moment.tz(value, tz).format(localeDateFormat)}
                  </Typography>
                  <Typography>{dayjs(value).tz(tz).format(localeTimeFormat)}</Typography>
                </>
              );
            } else {
              return '';
            }
          },
          filterType: 'custom',
          customFilterListOptions: {
            render: (dates) => filterChipText(dates, CLOSED_AT),
          },
          filterOptions: {
            logic: (date, filters) => shouldExcludeDate(date, filters),
            display: (filterList, onChange, index, column) => (
              <DateRangeFilter
                filterList={filterList}
                onChange={onChange}
                index={index}
                column={column}
                name={CLOSED_AT}
              />
            ),
          },
          hint: timeZoneHint(tz),
        },
      },
      { name: DAYS_OPEN },
      {
        name: STATUS,
        options: {
          customBodyRender: (status) => {
            if (status) {
              return (
                <StatusPill
                  status={status}
                  repair
                />
              );
            } else return '';
          },
          customFilterListOptions: {
            render: (v) => `${STATUS}: ${v}`,
          },
        },
      },
      {
        name: DESCRIPTION,
        options: {
          sort: false,
          filter: false,
          customBodyRender: (value) => {
            if (value) {
              let descriptionString = value;
              if (value.length > 30) {
                descriptionString = value.slice(0, 30);
                descriptionString += '...';
              }
              return (
                <CustomTooltip
                  title={(
                    <>
                      <Typography>{value}</Typography>
                    </>
                  )}
                >
                  <Typography style={styles.noWrap}>{descriptionString}</Typography>
                </CustomTooltip>
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: CREATED_BY,
        options: {
          customFilterListOptions: {
            render: (v) => `${CREATED_BY}: ${v}`,
          },
        },
      },
      {
        name: CLOSED_BY,
        options: {
          customBodyRender: (value) => {
            if (value) {
              let closedByString = value;
              if (value.length > 30) {
                closedByString = value.slice(0, 30);
                closedByString += '...';
              }
              return (
                <CustomTooltip
                  title={(
                    <>
                      <Typography>{value}</Typography>
                    </>
                  )}
                >
                  <Typography style={styles.noWrap}>{closedByString}</Typography>
                </CustomTooltip>
              );
            } else {
              return '';
            }
          },
        },
      },
      {
        name: VEHICLE,
        options: {
          customBodyRender: (value) => {
            const ticket = this.repairTickets.find(
              (repairTicket) => value === get(repairTicket, 'vehicle.humanId'),
            );
            if (value && ticket) {
              return (
                <Tooltip title="See Vehicle History">
                  <UILink
                    component="button"
                    variant="body2"
                    onClick={(e) => {
                      e.stopPropagation();
                      this.openVehicleModal(ticket.vehicle);
                    }}
                  >
                    {value}
                  </UILink>
                </Tooltip>
              );
            } else {
              return '';
            }
          },
          customFilterListOptions: {
            render: (v) => `${VEHICLE}: ${v}`,
          },
        },
      },
    ];

    return setUrlColumnFilters(addToColumnData(columnData, selectedFilters, primarySort));
  }

  get data() {
    const currentMoment = moment.utc().startOf('day');
    const data = this.repairTickets.map((ticket) => {
      const createdAt = moment.utc(ticket.createdAt).startOf('day');
      const closedAtTime = get(ticket, 'closedAt');
      const closedMoment = closedAtTime ? moment.utc(closedAtTime).startOf('day') : currentMoment;
      const daysOpen = closedMoment.diff(createdAt, 'days');

      const tableData = [
        get(ticket, '_id', ''),
        get(ticket, 'createdAt', ''),
        get(ticket, 'closedAt', ''),
        daysOpen,
        get(ticket, 'status', ''),
        get(ticket, 'description', ''),
        get(ticket, 'createdBy.name') || '',
        get(ticket, 'closedBy') || '',
        get(ticket, 'vehicle.humanId', ''),
      ];
      return tableData;
    });
    return data;
  }

  get vehicles() {
    const allVehicles = get(this, 'props.vehicles', []);
    return sortByIntegerString(allVehicles);
  }

  openVehicleModal(vehicle) {
    this.setState({
      showEditVehicleModal: true,
      editVehicle: vehicle,
    });
  }

  handleCloseVehicleModal() {
    this.setState({
      showEditVehicleModal: false,
      editVehicle: {},
    });
  }

  handleSaveVehicleModal(vehicle) {
    const {
      match,
      updateVehicle,
      fetchVehicles,
      showSuccessSnackbar,
      showErrorSnackbar,
    } = this.props;
    const marketId = get(match, 'params.marketId');
    let query;
    if (marketId) {
      query = { market_id: marketId };
    }

    const stateUpdate = {
      showEditVehicleModal: false,
      editVehicle: {},
    };
    updateVehicle(vehicle, vehicle._id)
      .then(() => {
        this.setState(stateUpdate, () => {
          showSuccessSnackbar(`Successfully updated vehicle ${vehicle.humanId}`);
        });
      })
      .then(() => fetchVehicles(query))
      .catch((error) => {
        this.setState(stateUpdate, () => {
          const errorResponseMsg = get(error, 'message') || error.toString();
          showErrorSnackbar(`Error updating vehicle ${vehicle.humanId}: ${errorResponseMsg}`);
        });
      });
  }

  // checks for repairId in URL, and opens modal
  handleOpenModalForRepairInURL() {
    const { repairTickets = [], match } = this.props;
    const repairTicketId = get(match, 'params.repairTicketId');
    if (!repairTicketId) return;

    const humanId = Number.parseInt(repairTicketId, 10);
    const target = find(repairTickets, (repair) => repair.humanId === humanId
      || repair._id === repairTicketId);
    if (target) this.handleViewRepairTicket(target._id);
  }

  handleCloseForm() {
    this.setState({ isRepairTicketFormOpen: false, editing: false, editRepairTicket: {} });
  }

  handleAddRepairTicket() {
    const { marketId } = get(this, 'props.match.params');

    this.setState({
      editRepairTicket: {
        _id: nanoid(24),
        marketId,
        status: OPEN,
        categoryId: NOT_DIAGNOSED_ID,
      },
      isRepairTicketFormOpen: true,
      editing: false,
    });
  }

  handleSaveForm(repairTicket, editing, commentOnly) {
    const {
      createRepairTicket,
      updateRepairTicket,
      fetchVehicles,
      fetchRepairTickets,
      showErrorSnackbar,
      showSuccessSnackbar,
    } = this.props;
    let query;
    const { marketId } = get(this, 'props.match.params');
    if (marketId) {
      query = { market_id: marketId };
    }

    const saveMethod = editing ? updateRepairTicket : createRepairTicket;
    saveMethod(repairTicket, repairTicket._id)
      .catch((error) => {
        const errorMessage = get(error, 'response.data.message');
        showErrorSnackbar(`Error ${editing ? 'updating' : 'creating'} repair ticket: ${errorMessage}`);
        throw error;
      })
      .then((data) => {
        const repairTicketFromServer = get(data, 'data', {});
        let stateUpdate = {
          editTask: repairTicketFromServer, // for commentOnly, reset editTask with serverTask to avoid mongo version fail
        };
        if (!commentOnly) {
          stateUpdate = {
            isRepairTicketFormOpen: false,
            editing: false,
            editRepairTicket: {},
          };
        }
        this.setState(stateUpdate, () => {
          showSuccessSnackbar(`Success ${editing ? 'updating' : 'creating'} repair ticket`);
          fetchVehicles(query);
          fetchRepairTickets(query);
        });
      });
  }

  handleViewRepairTicket(data) {
    const { repairTickets } = this.props;
    const matchingRepairTicket = find(repairTickets, (ticket) => ticket._id === data);
    this.setState({
      editRepairTicket: matchingRepairTicket,
      isRepairTicketFormOpen: true,
      editing: true,
    });
  }

  render() {
    const {
      isLoading, getTitleFromData, selectedMarket, adminUrls,
    } = this.props;
    const {
      editRepairTicket,
      editing,
      isRepairTicketFormOpen,
      editVehicle,
      showEditVehicleModal,
    } = this.state;
    const displayData = this.data;
    const tz = get(selectedMarket, 'tz', 'America/New_York');
    return (
      <WrappedSpinner isLoading={isLoading}>
        <Paper style={styles.root} sx={{ mt: 3 }}>
          <MUIDataTable
            title={getTitleFromData('Repair Tickets', displayData)}
            columns={this.columns}
            data={displayData}
            options={this.tableOptions}
          />
          <RepairTicketEditModal
            key={`repair-ticket-edit-form-${editRepairTicket._id}`}
            isOpen={isRepairTicketFormOpen}
            onCancel={this.handleCloseForm}
            onSave={this.handleSaveForm}
            repairTicket={editRepairTicket}
            editing={editing}
            vehicles={this.vehicles}
            tz={tz}
          />
          <VehicleEditForm
            key={`vehicle-edit-modal-${editVehicle._id}`}
            isOpen={showEditVehicleModal}
            onCancel={this.handleCloseVehicleModal}
            onSave={this.handleSaveVehicleModal}
            vehicle={editVehicle}
            adminUrls={adminUrls}
            showHistory
          />
        </Paper>
      </WrappedSpinner>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...marketActions,
  ...repairTicketActions,
  ...vehicleActions,
  ...snackbarActions,
}, dispatch);

const mapStateToProps = ({ markets, repairTickets, vehicles, settings }) => ({
  isLoading: repairTickets.isLoading || vehicles.isLoading,
  selectedMarket: markets.selectedMarket,
  repairTickets: repairTickets.marketRepairTickets,
  vehicles: vehicles.marketVehicles,
  adminUrls: settings.adminUrls,
});

export default withUrlProperties(
  withPersistentSettings(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(withRouter(RepairTicketsTable)),
    'RepairTicketsTable',
  ),
);
